<template>
  <div class="fb-post-snippet">
    <div class="fb-post-snippet__caption-textarea-wrap">
      <slot name="textarea" />
    </div>
    <!--<div class="fb-post-snippet__screen-size">{{ screenSize }}</div>-->

    <!-- Single photo: show uncropped -->
    <div class="fb-post-snippet__single-photo-media" v-if="props.imagesList.length === 1" :title="getReasonForMedia(props.imagesList[0]) || undefined">
      <MediaThumbnail :media-url="props.imagesList[0]" :lightboxKey="'FbPostPreview-' + props.lightboxKey" image-object-fit="contain" :image-max-height="singlePhotoImageMaxHeight" />
    </div>

    <!-- Photos grid -->
    <ul
      v-else
      :class="{
        'photos-grid': true,
        'photos-grid--1-photo-layout': props.imagesList.length === 1,
        'photos-grid--2-photos-layout': props.imagesList.length === 2,
        'photos-grid--3-photos-layout': props.imagesList.length === 3,
        'photos-grid--4-photos-layout': props.imagesList.length === 4,
        'photos-grid--5-photos-layout': props.imagesList.length >= 5,
        'fb-post-snippet__photos-grid': true,
      }"
    >
      <li class="photos-grid__photo-wrap" v-for="(mediaUrl, index) in props.imagesList" :key="index" :title="getReasonForMedia(mediaUrl) || null">
        <div v-if="props.imagesList.length > 5 && index === 4" class="photos-grid__counter-overlay">+{{ props.imagesList.length - 5 }}</div>
        <MediaThumbnail
          :class="{
            'photos-grid__photo': true,
            'photos-grid__photo--hidden': index >= 5,
          }"
          :media-url="mediaUrl"
          :lightboxKey="'FbPostPreview-' + props.lightboxKey"
        />
      </li>
    </ul>
    <!-- / Photos grid -->
  </div>
</template>

<script setup lang="ts">
import { computed, inject, Ref } from "vue";

// Components
import MediaThumbnail from "@components/MediaThumbnail.vue";

// Types
import { ScreenSize } from "@contracts/screenSize";
import { ImageSelectAIDebugData, SocialPostPublishType } from "@contracts/shareStop";
import { getSingleSocialPreviewMediaMaxHeight } from "@helpers/SocialPostUIHelper";

const props = withDefaults(
  defineProps<{
    imagesList: Array<string>;
    lightboxKey: string;
    debugData?: ImageSelectAIDebugData | null;
    postType?: SocialPostPublishType | null;
  }>(),
  {
    imagesList: () => [],
    lightboxKey: "",
    debugData: null,
    postType: null,
  }
);

const screenSize = inject("screenSize") as Ref<ScreenSize>;

const singlePhotoImageMaxHeight = computed(() => getSingleSocialPreviewMediaMaxHeight(screenSize.value));

function getReasonForMedia(mediaUrl: string): string | null {
  if (!props.debugData) return null;
  // Show the reason for the image selection in debug mode
  // This makes it much easier to tell if the off-by-1 issue occurred
  const selected = props.debugData?.excludeFromIndex?.debugInfo?.imagesSelected || [];
  const match = selected.find((item: any) => item.filename === mediaUrl);

  if (match) return `${match.index}: ${match.reason}`;
  return null;
}
</script>

<style scoped lang="scss">
@mixin placeholderElement {
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.1);
}

// Photos grid ================================================================
.photos-grid {
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  list-style: none;

  &--1-photo-layout {
    .photos-grid__photo-wrap {
      width: 100%;
      aspect-ratio: 100/66;
    }
  }

  &--2-photos-layout {
    .photos-grid__photo-wrap {
      width: calc(50% - 1px);
      margin-right: 2px;
      aspect-ratio: 1/1;
    }
    .photos-grid__photo-wrap:last-child {
      margin-right: 0;
    }
  }

  &--3-photos-layout {
    .photos-grid__photo-wrap:nth-child(1) {
      width: 100%;
      aspect-ratio: 2/1;
      margin-bottom: 4px;
    }
    .photos-grid__photo-wrap:nth-child(2) {
      width: calc(50% - 2px);
      max-height: 150px;
      margin-right: 4px;
    }
    .photos-grid__photo-wrap:nth-child(3) {
      width: calc(50% - 2px);
      max-height: 150px;
    }
  }

  &--4-photos-layout {
    .photos-grid__photo-wrap:nth-child(1) {
      width: 100%;
      aspect-ratio: 100/50;
      margin-bottom: 4px;
    }
    .photos-grid__photo-wrap:nth-child(2),
    .photos-grid__photo-wrap:nth-child(3) {
      aspect-ratio: 1/1;
      width: calc(33.3% - 3px);
      margin-right: 4px;
    }
    .photos-grid__photo-wrap:nth-child(4) {
      aspect-ratio: 1/1;
      width: calc(33.3% - 2px);
    }
  }

  &--5-photos-layout {
    .photos-grid__photo-wrap:nth-child(1) {
      width: calc(50% - 2px);
      aspect-ratio: 1/1;
      margin: 0 4px 4px 0;
    }

    .photos-grid__photo-wrap:nth-child(2) {
      width: calc(50% - 2px);
      aspect-ratio: 1/1;
      margin-bottom: 4px;
    }

    .photos-grid__photo-wrap:nth-child(3),
    .photos-grid__photo-wrap:nth-child(4) {
      aspect-ratio: 1/1;
      width: calc(33.3% - 3px);
      margin-right: 4px;
    }

    .photos-grid__photo-wrap:nth-child(5) {
      aspect-ratio: 1/1;
      width: calc(33.3% - 2px);
    }
  }

  &__photo-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 0;
    overflow: hidden;
  }

  &__photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: flex;
    justify-content: center;
    align-items: center;

    &--hidden {
      display: none;
    }

    :deep(.media-thumbnail-checkbox__video-wrap) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    :deep(.media-thumbnail-checkbox__video) {
      display: flex;
      justify-content: center;
      align-items: center;

      a {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }
  }

  &__counter-overlay {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 1;
    color: #fff;
    font: bold 24px/24px sans-serif;
    background: rgba(0, 0, 0, 0.5);
    pointer-events: none;
  }
}

// Fb post snippet ============================================================
.fb-post-snippet {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__user-info-skeleton {
    width: calc(100% - 32px);
    margin-bottom: 13px;
  }

  &__caption-textarea-wrap {
    width: calc(100% - 32px);
    margin-bottom: 14px;

    :deep(textarea) {
      width: 100%;
      height: 140px;
      min-height: 140px;
      box-sizing: border-box;
      border: 1px #ababab solid;
      border-radius: 6px;
      color: #5b5b5b;
      font-family: sans-serif;
      transition: none !important;
    }
  }

  &__photos-grid {
    width: 100%;
  }

  &__screen-size {
    width: 100%;
    margin-bottom: 8px;
    color: #6a6a6a;
    font: 12px/16px sans-serif;
    text-align: center;
  }

  &__single-photo-media {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__footer-skeleton {
    width: calc(100% - 26px);
  }
}
</style>
